import { useCallback, useEffect, useRef } from 'react';

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]';

const focusableIn = (element) =>
  element?.matches(FOCUSABLE) ? element : (element?.querySelector(FOCUSABLE) ?? null);

/**
 * Keeps keyboard focus when a row control removes itself: '+N owners above' and 'Show all' are
 * gone once the next graph arrives. Call request(nodeId) before the action; after the next graph
 * the row with that data-node-id (its first focusable element), or the container, gets focus.
 *
 * @param {{ current: HTMLElement|null }} containerRef - the list or table that holds the rows
 * @param {Object|null} graph - PreparedGraph; a new one runs the pending focus request
 * @returns {(nodeId?: string|null) => void} request; null (the default) focuses the container
 */
export const useRestoreNodeFocus = (containerRef, graph) => {
  const pendingRef = useRef(undefined);
  useEffect(() => {
    if (pendingRef.current === undefined || !containerRef.current) return;
    const nodeId = pendingRef.current;
    pendingRef.current = undefined;
    const row = [...containerRef.current.querySelectorAll('[data-node-id]')].find(
      (element) => element.dataset.nodeId === nodeId
    );
    (focusableIn(row) ?? containerRef.current).focus({ preventScroll: true });
  }, [graph, containerRef]);
  return useCallback((nodeId = null) => {
    pendingRef.current = nodeId;
  }, []);
};
